import { Check } from "lucide-react";
import { useEffect, useRef } from "react";
import { cn } from "@repo/utils";
import type { SelectOption, SelectOptionValue } from "./select.js";

type Props = {
  options: SelectOption<SelectOptionValue>[];
  selectedValues: string[];
  handleSelect: (value: string) => void;
  highlightedIndex: number;
  enableScrollToSelected?: boolean;
};

export const SelectOptions = ({
  options,
  selectedValues,
  handleSelect,
  highlightedIndex,
  enableScrollToSelected,
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    if (!enableScrollToSelected) return;
    if (hasScrolledRef.current) return;

    const firstSelected = selectedValues[0];
    if (!firstSelected) return;

    const selectedElement = containerRef.current?.querySelector(
      `[data-select-value="${firstSelected}"]`
    );

    if (selectedElement instanceof HTMLElement) {
      selectedElement.scrollIntoView({ block: "start" });
      hasScrolledRef.current = true;
    }
  }, [enableScrollToSelected, selectedValues]);

  return (
    <div ref={containerRef}>
      {options.length === 0 ? (
        <div className="px-3 py-2 text-sm text-gray-500">No options available</div>
      ) : (
        options.map((option, index) => {
          const optionValueStr = String(option.value);
          const isSelected = selectedValues.includes(optionValueStr);
          const isHighlighted = index === highlightedIndex;

          return (
            <button
              id={`select-option-${index}`}
              key={option.value}
              type="button"
              onClick={() => !option.disabled && handleSelect(optionValueStr)}
              disabled={option.disabled}
              data-select-value={option.value}
              className={cn(
                "flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors",
                {
                  "cursor-not-allowed opacity-50": option.disabled,
                  "bg-gray-100": !option.disabled && isHighlighted,
                  "hover:bg-gray-100": !option.disabled,
                  "bg-blue-50 text-blue-900": isSelected,
                  "bg-blue-100": isSelected && isHighlighted,
                }
              )}
            >
              <span>{option.label}</span>
              <span className="flex items-center gap-1">
                {option.suffix}
                {isSelected && <Check className="h-4 w-4 text-blue-600" />}
              </span>
            </button>
          );
        })
      )}
    </div>
  );
};
